import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FileDragAndDropComponent } from '@lockton-apps/shared/ui/lui';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import {
  castArray,
  compact,
  flatMap,
  includes,
  join,
  map,
  reject,
} from 'lodash';

interface ImageFileInputProps extends FormlyFieldProps {
  accept?: string[];
  multiple: boolean;
  tooltip: string;
  hint?: string;
}

interface ImageFileInputFile {
  name: string;
  src: string;
  isImage: boolean;
}

@Component({
    selector: 'lockton-apps-image-file-input-type',
    templateUrl: './image-file-input-type.component.html',
    styleUrls: ['./image-file-input-type.component.scss'],
    standalone: false
})
export class ImageFileInputTypeComponent
  extends FieldType<FieldTypeConfig<ImageFileInputProps>>
  implements OnInit, AfterViewInit
{
  @ViewChild(FileDragAndDropComponent, { static: true })
  dragAndDropComponent!: FileDragAndDropComponent;

  rejectedFileNames: string[] = [];
  acceptedFormats: string[] = [];
  acceptedFormatNames!: string;

  constructor(private readonly cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    this.acceptedFormats = flatMap(this.props.accept, 'extensions');
    this.acceptedFormatNames = join(map(this.props.accept, 'name'), ', ');
  }

  ngAfterViewInit(): void {
    const files = this.readImageFileInputFiles(this.formControl.value);
    this.dragAndDropComponent.value = files;
  }

  async onFilesAdded(files: File | File[]): Promise<void> {
    const _files: ImageFileInputFile[] = await this.readFiles(
      compact(castArray(files))
    );

    this.rejectedFileNames = [];
    this.setValue(_files);
  }

  onFilesRejected(files: File | File[]): void {
    this.rejectedFileNames = map(castArray(files), 'name');
  }

  removeFile(inputFile: ImageFileInputFile) {
    const file = new File([inputFile.src], inputFile.name);
    this.dragAndDropComponent.removeFile(file);
  }

  onFilesRemoved(files: File[]): void {
    if (!this.props.multiple) {
      return this.formControl.reset();
    }
    this.setValue(
      reject(this.formControl.value, (file) =>
        includes(map(files, 'name'), file.name)
      )
    );
  }

  private setValue(files: ImageFileInputFile[]): void {
    this.formControl.setValue(files);
    this.cd.markForCheck();
  }

  private async readFiles(files: File[]): Promise<ImageFileInputFile[]> {
    return await Promise.all(
      map(files, (file: File) => {
        return new Promise(
          (resolve: (file: ImageFileInputFile) => void, reject: () => void) => {
            const reader: FileReader = new FileReader();

            reader.onload = (event: ProgressEvent<FileReader>) => {
              return resolve({
                name: file.name,
                src: (event.target as FileReader).result as string,
                isImage: !!file.type.match(/image\//),
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }
        );
      })
    );
  }

  private readImageFileInputFiles(files: ImageFileInputFile[]): File[] {
    return map(files, (file) => {
      return new File([file.src], file.name);
    });
  }
}
